<template>
  <layout>
    <h1 class="w-full adm-container flex justify-center my-8">Change your Password</h1>
    <div class="w-full adm-container flex justify-center form-container">
      <div class="change-password-form-container">
        <pt-form>
          <pt-form-group
            :invalid-feedback="currentPasswordError"
            label="Current Password"
            label-for="currentPassword"
          >
            <pt-form-input
              id="currentPassword"
              v-model="$v.currentPassword.$model"
              type="password"
              placeholder="Enter your current password"
            />
          </pt-form-group>
          <pt-form-group
            :invalid-feedback="newPasswordError"
            :hint="hintMsg"
            label="New Password"
            label-for="newPassword"
          >
            <pt-form-input
              id="newPassword"
              v-model="$v.newPassword.$model"
              type="password"
              placeholder="Enter your new password"
            />
          </pt-form-group>
          <pt-form-group
            :invalid-feedback="retypePasswordError"
            label="Retype New Password"
            label-for="retypeNewPassword"
          >
            <pt-form-input
              id="retypeNewPassword"
              v-model="$v.retypeNewPassword.$model"
              type="password"
              placeholder="Enter your new password again"
            />
          </pt-form-group>
        </pt-form>
      </div>
    </div>
    <div class="w-full adm-container flex justify-center">
      <div style="width: 520px">
        <pt-button
          :disabled="resetBtnDisabled"
          variant="primary"
          @action="changePassword"
        >
          Change
          <pt-loader
            v-show="resetBtnDisabled"
            icon-class="fa-spinner"
          />
        </pt-button>
      </div>
    </div>
  </layout>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { PtForm, PtFormGroup, PtFormInput } from '@pathao/ui-components/esm/FormComponents';
import { PtButton } from '@pathao/ui-components/esm/Button';
import { PtLoader } from '@pathao/ui-components/esm/Loader';
import { mapActions } from 'vuex';
import Config from '@config';
import { PASSWORD_HINT_MSG } from '@constants';
import Layout from '@layouts/main';
import router from '@router';
import makeRequest from '@utils/apiRequest';
import policy from '@utils/password-policy';

export default {
  name: 'ChangePassword',
  page: {
    title: 'Change Password',
  },
  components: { PtButton, PtForm, PtFormGroup, PtFormInput, Layout, PtLoader },
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentPassword: null,
      newPassword: null,
      retypeNewPassword: null,
      resetBtnDisabled: false,
      hintMsg: PASSWORD_HINT_MSG,
    };
  },
  validations: {
    currentPassword: { required },
    newPassword: { required, policy },
    retypeNewPassword: { required },
  },
  computed: {
    currentPasswordError() {
      if (!this.$v.currentPassword.$dirty) {
        return null;
      }
      if (!this.$v.currentPassword.required) {
        return `Current password cannot be empty`;
      }
    },
    newPasswordError() {
      return this.computePasswordErrorMsg(this.$v.newPassword);
    },
    retypePasswordError() {
      if (!this.$v.retypeNewPassword.$dirty) {
        return null;
      }

      if (!this.$v.retypeNewPassword.required) {
        return `Password cannot be empty`;
      } else if (this.$v.retypeNewPassword.$model !== this.$v.newPassword.$model) {
        return `Both passwords should match`;
      }

      return null;
    },
  },
  methods: {
    ...mapActions({
      updateUserPasswordChange: 'auth/updateUserPasswordChange',
    }),
    computePasswordErrorMsg(field) {
      if (!field.$dirty) {
        return null;
      }

      if (!field.required) {
        return `Password cannot be empty`;
      } else if (!field.policy) {
        return `Password must have correct format`;
      }

      return null;
    },
    changePassword(event) {
      event.preventDefault();
      this.resetBtnDisabled = true;
      this.$v.currentPassword.$touch();
      this.$v.newPassword.$touch();
      this.$v.retypeNewPassword.$touch();

      if (this.$v.currentPassword.$invalid || this.$v.newPassword.$invalid || this.$v.retypeNewPassword.$invalid) {
        this.$notification.error('Please correct all the fields');
        this.resetBtnDisabled = false;

        return null;
      }

      const requestMeta = {
        url: `${Config.api.baseURL}api/change-password`,
        method: 'PUT',
        data: {
          old_password: this.currentPassword,
          new_password: this.newPassword,
        },
      };

      makeRequest(requestMeta)
        .then((response) => {
          this.resetBtnDisabled = false;
          this.$notification.success('Password changed successfully');
          this.updateUserPasswordChange();
          setTimeout(() => {
            router.push('captain-management/drivers');
          }, 1500);
        })
        .catch((errorResponse) => {
          this.$notification.error('Current password does not match');
          this.resetBtnDisabled = false;
          console.error(errorResponse);
        });
    },
  },
};
</script>
